/**
 * ADR-0216 — persistent read-only banner rendered while the merchant
 * dashboard is running inside a staff support session. Reads
 * `user.support_session` (folded into the User by `real-auth.ts`) and
 * shows the account name + a live countdown to the 60-minute expiry.
 *
 * "End session" fires the existing sign-out flow — the same one the
 * merchant uses on their own account — so the staff has a one-click
 * exit that doesn't disturb any merchant surface.
 */

import { useEffect, useState } from 'react';

import { useAuth } from '../app/auth-context.js';
import { useC } from '../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

function formatRemaining(expires_at: string, now: number): string {
  const ms = new Date(expires_at).getTime() - now;
  if (ms <= 0) return 'expired';
  const mins = Math.floor(ms / 60_000);
  return `${mins} min`;
}

export function SupportSessionBanner(): JSX.Element | null {
  const auth = useAuth();
  const C = useC();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (auth.state.kind !== 'authenticated') return;
    if (auth.state.user.support_session == null) return;
    const id = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, [
    auth.state.kind,
    auth.state.kind === 'authenticated'
      ? auth.state.user.support_session
      : null,
  ]);

  if (auth.state.kind !== 'authenticated') return null;
  const ss = auth.state.user.support_session;
  if (ss == null) return null;

  const remaining = formatRemaining(ss.expires_at, now);

  return (
    <div
      role="alert"
      style={{
        background: C.amber,
        color: C.ink,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        borderBottom: `1px solid ${C.border}`,
        fontFamily: sansFont,
        fontSize: 13,
      }}
    >
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 2,
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        Support session
      </span>
      <span style={{ opacity: 0.85 }}>
        {ss.allow_changes === true ? (
          <>
            Editing <strong>{ss.account_name}</strong> as Sill support. Changes are shown to the merchant as made by
            Sill support; payments, billing, team and deletion stay locked. Ends in {remaining}.
          </>
        ) : (
          <>
            Read-only view of <strong>{ss.account_name}</strong>. Ends in {remaining}.
          </>
        )}
      </span>
      <button
        type="button"
        onClick={() => void auth.signOut()}
        style={{
          marginLeft: 'auto',
          background: C.ink,
          color: C.amber,
          fontFamily: sansFont,
          fontSize: 12,
          fontWeight: 600,
          padding: '4px 12px',
          border: 'none',
          borderRadius: 2,
          cursor: 'pointer',
        }}
      >
        End session
      </button>
    </div>
  );
}
